import { useEffect, useMemo } from 'react';

import * as clientSlice from '../data/client.slice';
import { phoneUtils } from '../services/phoneUtils';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useCommonTranslation } from './useCommonTranslation';

/**
 * Interpolation values for the `{{clientName}}` / `{{clientNumber}}`
 * placeholders in client-branded validation messages. Spread into the `t()`
 * options, so `context` (when set) selects the `*_noPhone` message variants —
 * the ones without the "at {{clientNumber}}" clause.
 */
export type ClientMemberContactInterpolation = {
  clientName: string;
  clientNumber: string;
  context?: 'noPhone';
};

/**
 * The client's member-facing contact (name + "Member Contact Phone Number"
 * client setting), fetched once per clientBC + servicePlanID via
 * `fetchClientMemberContactAsync` and cached in the client slice. The
 * setting's E.164 value is formatted for display, e.g. `+18444452824` →
 * `(844) 445-2824`.
 *
 * Pass the passenger's `clientBC` and service plan (the back end resolves the
 * phone-number setting per plan). Neither has a fallback: until both are
 * available — i.e. until the passenger record loads — no fetch happens.
 *
 * Returns one of exactly three shapes, so a raw `{{clientName}}` /
 * `{{clientNumber}}` placeholder can never leak into a rendered message:
 *  - name + phone both present → both interpolation values, base message;
 *  - name present, phone missing → `context: 'noPhone'` steers `t()` to the
 *    `*_noPhone` variants (no "at {{clientNumber}}" clause);
 *  - name missing (fetch pending/failed, or not in the response) → the
 *    generic `clientSupport.fallbackClientName` with the `*_noPhone` variant,
 *    regardless of whether a phone number was received.
 */
export function useClientMemberContact(
  clientBC?: string | null,
  servicePlanID?: string | null,
): ClientMemberContactInterpolation {
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();
  const contactKey =
    clientBC && servicePlanID
      ? clientSlice.memberContactKey(clientBC, servicePlanID)
      : undefined;

  const contactsByClientPlan = useAppSelector(
    clientSlice.selectMemberContactByClientPlan,
  );
  const loadingByClientPlan = useAppSelector(
    clientSlice.selectLoadingMemberContactByClientPlan,
  );

  useEffect(() => {
    if (!clientBC || !servicePlanID || !contactKey) return;
    // undefined = never requested; null = settled miss (don't refetch).
    if (contactsByClientPlan[contactKey] !== undefined) return;
    if (loadingByClientPlan[contactKey]) return;
    dispatch(
      clientSlice.fetchClientMemberContactAsync(clientBC, servicePlanID),
    );
  }, [
    dispatch,
    clientBC,
    servicePlanID,
    contactKey,
    contactsByClientPlan,
    loadingByClientPlan,
  ]);

  const contact = contactKey ? contactsByClientPlan[contactKey] : undefined;

  return useMemo(() => {
    const clientName = contact?.clientName;
    const clientNumber =
      (contact?.phoneNumber &&
        phoneUtils.formatPhoneString(contact.phoneNumber, false, false)) ||
      '';
    if (clientName && clientNumber) {
      return { clientName, clientNumber };
    }
    // Without a name the message can't be client-branded, so the phone number
    // (even if received) is withheld too: a member shouldn't be told to call a
    // number that can't be attributed to their client.
    return {
      clientName: clientName || t('clientSupport.fallbackClientName'),
      // Unused by the `_noPhone` variants; blank keeps a namespace that's
      // missing its variant from rendering a raw {{clientNumber}}.
      clientNumber: '',
      context: 'noPhone' as const,
    };
  }, [contact, t]);
}
